import { createContext, FC, forwardRef, MutableRefObject, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Button, FormHelperText, IconButton, MenuItem, Popper, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddGroupIcon from '@mui/icons-material/PlaylistAdd';
import { groupRules, operatorRules, pickerRules, RuleDisplayKind, RuleDisplayTree } from "./RuleDisplayPrimitives";
import { SimpleOption2 } from "../governance/search/AmbientFacilitiesContext";
import { useRecordContext } from "ra-core";
import { ClassificationRuleDto } from "~/gen/api/securefile";


export const RuleDisplayRoot: FC = () => {
  const { states, switchRootKind, addChildToVirtualRoot, addChildGroupToVirtualRoot, registerRef } = useContext(RuleComposeContext)
  const showVirtualRoot = () => (
    <>
      <Box className="group-actions-placement" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
        {!states.isVirtualRootOperator &&
          <Select value={states.virtualRootGroup.Kind} size="small" sx={{ mr: 2 }}
            onChange={(e) => switchRootKind(e.target.value as any)}
          >
            <MenuItem value="GroupAnd">Group And</MenuItem>
            <MenuItem value="GroupOr" >Group Or</MenuItem>
          </Select>}  
        <Tooltip title="add">
          <IconButton color="primary" onClick={addChildToVirtualRoot}>
            <AddIcon /></IconButton>
        </Tooltip>
        <Tooltip title="add subgroup">
          <IconButton color="primary" onClick={addChildGroupToVirtualRoot}>
            <AddGroupIcon /></IconButton>
        </Tooltip>
      </Box>
      <GroupRule rule={states.virtualRootGroup} hideBtns={true} getCtrlsRef={registerRef} />
    </>)

  return (
    <Box
      sx={{
        '& .layout': { display: 'grid', gridTemplateColumns: '1fr 48px', },
        '& .close-btn-placement': { gridColumn: '2', gridRow: '1', },
        '& .group-actions-placement': { gridColumn: '1', gridRow: '1', },
        '& .group-children-placement': { gridColumn: '1/3', gridRow: '2',
          '& div.alt-line': {
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            bgcolor: 'background.paper',
          }, 
        },
        '& .operator-placement': { gridColumn: '1', gridRow: '1', },
      }}
    >
      {!states.usingVirtualRoot &&
        <GroupRule rule={states.rule} getCtrlsRef={registerRef} />}
      {states.usingVirtualRoot && states.virtualRootGroup.Kind === 'GroupAnd' && showVirtualRoot()}
      {states.usingVirtualRoot && states.virtualRootGroup.Kind === 'GroupOr' && 
        <Box>
          {showVirtualRoot()}
        </Box>}
    </Box>
  )
}

export interface RuleComposeContextProps {
  registerValidator: (x: ()=>Promise<boolean>) => number;
  unregisterValidator: (x: number) => void;
  validate: () => Promise<boolean>;
  states: {
    createMode: boolean;
    rule: RuleDisplayTree;
    usingVirtualRoot: boolean;
    isVirtualRootOperator: boolean;
    virtualRootGroup: RuleDisplayTree;
    original: RuleDisplayTree;
    validationTouched: boolean;
    isDirty: boolean;
  };
  trackRuleTreeDirty: (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => void;
  getComposedRule: () => RuleDisplayTree|null;
  registerRef: (cb: () => GroupRuleContextProps) => void;
  switchRootKind: (k: "GroupAnd" | "GroupOr") => void;
  addChildToVirtualRoot: () => void;
  addChildGroupToVirtualRoot: () => void
}

const RuleComposeContext = createContext<RuleComposeContextProps>({} as any)

export const RuleComposeContextProvider : FC<{values:RuleComposeContextProps}> = ({values, children}) => {
  return (
    <RuleComposeContext.Provider value={values}>
      <NestedGroupContext.Provider value={
        useMemo(() => ({ level: 0, propagateUp: values.trackRuleTreeDirty }), 
                [values.trackRuleTreeDirty])
      }>
        {children}
      </NestedGroupContext.Provider>
    </RuleComposeContext.Provider>
  )
}

export const  useRuleComposeController = (): RuleComposeContextProps => {
  const record = useRecordContext()
  const dto: ClassificationRuleDto|undefined = record as any

  const rule = useMemo(() => {
    return JSON.parse(dto?.ruleDisplayTree ?? '{}') as RuleDisplayTree
  }, [dto?.ruleDisplayTree])

  const [states, setStates] = useState(() => {
    const createMode = !(rule.Kind)
    const usingVirtualRoot = createMode || !groupRules.has(rule?.Kind as any)
    const isVirtualRootOperator = usingVirtualRoot
    const virtualRootGroup: RuleDisplayTree = { 
      Kind: 'GroupAnd', 
      Group: [!createMode ? (rule) : 
        { 
          Kind: 'Equals', 
          Left: {Kind: 'PickValue', Value: 'FileName'}, 
          Right: {Kind: 'PickConstant', Value: ''} 
        }
      ] 
    }    
    return ({
      createMode,
      rule,
      usingVirtualRoot,
      isVirtualRootOperator,
      virtualRootGroup,
      original: virtualRootGroup,
      validationTouched: false,
      isDirty: false,
    })
  })
  const validatorsRef = useRef([] as (()=>Promise<boolean>)[])

  const getCtrlsRef = useRef<(()=>GroupRuleContextProps)>(() => (null as any))

  const methods = useMemo(() => {
    const registerValidator = (validator: ()=>Promise<boolean>) => {
      return validatorsRef.current.push(validator) - 1
    }
    const unregisterValidator = (n: number) => {
      delete validatorsRef.current[n]
    }
    const validate = async () => {
      let result = true
      for (const validator of validatorsRef.current.filter(x => x)) {
        result = (await validator()) && result
      }
      setStates(v => ({...v, validationTouched: true}))
      return result
    }
    const trackRuleTreeDirty = (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => {
      setStates(v => {
        if (!v.usingVirtualRoot) {
          v.isDirty = prevRule !== updRule
        } else if (v.isVirtualRootOperator) {
          v.isDirty = prevRule !== updRule
        } else {
          // v.isVirtualRootOperator === false
          v.isDirty = true
        }
        return {...v}
      })
    }
    const registerRef = (cb:() => GroupRuleContextProps) => { getCtrlsRef.current = cb };
    const switchRootKind = (k: 'GroupAnd'|'GroupOr') => {
      setStates(v => {
        if (v.virtualRootGroup.Kind === k) { return v }
        const tree = getCtrlsRef.current().states.composedRule
        const flipKind = (t:RuleDisplayTree) => {
          if (t.Kind === 'GroupAnd') {
            t.Kind = 'GroupOr'
          } else if (t.Kind === 'GroupOr') {
            t.Kind = 'GroupAnd'
          } else { return }
          for (const s of t.Group ?? []) {
            flipKind(s)
          }
        }
        flipKind(tree)
        return {...v, virtualRootGroup: {...tree}}
      })
    }  
    const addChildToVirtualRoot = () => {
      getCtrlsRef.current().addChildRule("Equals")
      setStates(v => {
        if (!v.isVirtualRootOperator) { return v }
        return {...v, isVirtualRootOperator: false }
      })
    }
    const addChildGroupToVirtualRoot = () => {
      setStates(v => {
        setTimeout(() => {
          getCtrlsRef.current().addChildRule(v.virtualRootGroup.Kind === 'GroupAnd' ? "GroupOr" : "GroupAnd")
        })
        if (!v.isVirtualRootOperator) { return v }
        return {...v, isVirtualRootOperator: false }
      })
    }
    return {
      registerValidator,
      unregisterValidator,
      validate,
      trackRuleTreeDirty,
      registerRef,
      switchRootKind,
      addChildToVirtualRoot,
      addChildGroupToVirtualRoot,
    }
  }, [])

  return useMemo(() => ({
    states, 
    getComposedRule: () => {
      const root = getCtrlsRef.current().states.composedRule
      let composedRule = root
      if (states.usingVirtualRoot && states.isVirtualRootOperator) {
        composedRule = root.Group![0]
      }
      const simplify = (t: RuleDisplayTree): RuleDisplayTree|null => {
        if (!groupRules.has(t.Kind)) { return t }
        if (!t.Group) { return null }
        if (t.Group.length === 0) { return null }
        else if (t.Group.length === 1) {
          return simplify(t.Group[0])
        } else {
          const group1: RuleDisplayTree[] = t.Group.map(simplify).filter(x => x) as any
          return (
            group1.length === 0 ? null
          : group1.length === 1 ? group1[0]
          : {...t, Group: group1 })
        }
      }
      return composedRule ? simplify(composedRule) : null
    },
    ...methods
  }), [states, methods])
}


const RuleDisplay: FC<{rule:RuleDisplayTree}> = ({rule}) => {
  if (pickerRules.has(rule.Kind)) { return null; }

  return (
    groupRules.has(rule.Kind)    
      ? <GroupRule rule={rule} />
      : <OperatorRule rule={rule} />
  )
}


const GroupRule: FC<{
  rule:RuleDisplayTree, 
  hideBtns?:boolean,
  getCtrlsRef?: (x:()=>GroupRuleContextProps)=>void,
}> = ({rule, hideBtns, getCtrlsRef}) => {
  const controls = useGroupRuleController(rule)
  const { 
    states: { prevGroup, removedRules, added }, 
    nextLevel, color,
    updateChildRule,
    removeChildRule,
    restoreChildRule,
    addChildRule,
  } = controls
  const ctrlRef = useRef(controls)
  ctrlRef.current = controls
  if (getCtrlsRef) {
    getCtrlsRef(() => ctrlRef.current)
  }

  const childLine = (r: RuleDisplayTree, isAltLine: boolean, isGroup: boolean) => (
    <Box sx={{ width: '100%', my: 0.5, py: 0.5, }} className={`layout${isAltLine ? ' alt-line' : ''}`} >
      <RuleDisplay rule={r} />
      <Tooltip title={isGroup ? "remove subgroup" : "remove"}>
        <IconButton aria-label={isGroup ? "remove subgroup" : "remove"} sx={{ mr: 1, }} className="close-btn-placement"
          onClick={() => removeChildRule(r)} 
        >
          <CloseIcon htmlColor={(isGroup ? colors[nextLevel % colors.length] : undefined)}  />
        </IconButton>
      </Tooltip>
    </Box>);

  return (
    <>
      {!hideBtns &&
        <Box className="group-actions-placement" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
          <Typography variant="subtitle2" sx={{ mr: 4, color }}>
            {rule.Kind === "GroupAnd" ? "Group And" : "Group Or"}
          </Typography>
          <Tooltip title="add">
            <IconButton onClick={() => addChildRule("Equals")} color="primary">
              <AddIcon /></IconButton>
          </Tooltip>
          <Tooltip title="add subgroup">
            <IconButton onClick={() => addChildRule(rule.Kind === "GroupAnd" ? "GroupOr" : "GroupAnd")} color="primary">
              <AddGroupIcon /></IconButton>
          </Tooltip>
        </Box>}
      <Box className="group-children-placement" sx={{ borderLeft: `2px solid ${color}`, pl: 1.5, bgcolor: 'background.default', my: 0.5, py: 0.5 }}>
        <NestedGroupContext.Provider value={{propagateUp: updateChildRule, level: nextLevel}}>
          {prevGroup.map((r, i) => {
            const isRemoved = removedRules.indexOf(r) > -1
            const isAltLine = i % 2 === 1 && !r.Group
            const isGroup = !!r.Group
            return (
              <Box key={`p${i}`} >
                {!isRemoved 
                  ? childLine(r, isAltLine, isGroup)
                  : <Stack direction="row" justifyContent="space-between" alignItems="center" my={1} mr={1}>
                      {isGroup  
                        ? <Typography variant="body2" sx={{ textDecoration: 'line-through' }} color={colors[nextLevel % colors.length]} >
                            {r.Kind === "GroupAnd" ? "Group And" : "Group Or"}
                          </Typography>
                        : <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                            <Typography component="span" color="success.light">{pvOptions.find(x => x.key === r.Left!.Value)!.text} </Typography>
                            <Typography component="span" color="info.light" >{opOptions.find(x => x.key === r.Kind)!.text}</Typography> …
                          </Typography>}
                      <Button onClick={() => restoreChildRule(r)}>Restore</Button>
                    </Stack>
                  }
              </Box>
            )
          })}
          {added.stubs.map((r, i) => {
            const isAltLine = (prevGroup.length + i) % 2 === 1 && !r.Group
            const isGroup = !!r.Group
            return (
              <Box key={`a${(r as any).n}`}>
                {childLine(r, isAltLine, isGroup)}
              </Box>
            )
          })}
        </NestedGroupContext.Provider>
      </Box>
    </>
  )
}

// ?? composing rule ??

interface GroupRuleContextProps {
  addChildRule: (kind: RuleDisplayKind) => void;
  removeChildRule: (r: RuleDisplayTree) => void;
  restoreChildRule: (r: RuleDisplayTree) => void;
  updateChildRule: (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => void;
  states: {
    prevGroup: RuleDisplayTree[];
    removedRules: RuleDisplayTree[];
    updatedRules: RuleDisplayTree[];
    added: {
      stubs: RuleDisplayTree[];
      rules: RuleDisplayTree[];
    };
    composedRule: RuleDisplayTree;
  },
  nextLevel: number;
  color: string;
}

interface States {
  prevGroup: RuleDisplayTree[];
  removedRules: RuleDisplayTree[];
  updatedRules: RuleDisplayTree[];
  added: {
    stubs: RuleDisplayTree[];
    rules: RuleDisplayTree[];
  };
  composedRule: RuleDisplayTree;
}

const useGroupRuleController = (rule:RuleDisplayTree): GroupRuleContextProps => {
  const [states, setStates] = useState({
    prevGroup: rule.Group ?? [],
    removedRules: [] as RuleDisplayTree[], // this field is expected to store elems from prevGroup
    updatedRules: [] as RuleDisplayTree[], // this field is expected to store the children's composedRules; it's a sparse array
    added: {
      n: 0,
      stubs: [] as RuleDisplayTree[],
      rules: [] as RuleDisplayTree[],
    },
    composedRule: { Kind: rule.Kind, Group: [...rule.Group!] } as RuleDisplayTree, // this field's obj ref should remain stable
  })
  const { propagateUp, level } = useContext(NestedGroupContext)

  const modifiers = useMemo(() => {
    const recompose = (v: States) => {
      const arr = [...v.prevGroup];
      v.updatedRules.forEach((u, j) => arr[j] = u);
      arr.push(...v.added.rules.filter(x => Boolean(x)));
      v.removedRules.forEach((rm) => {
        const k = arr.indexOf(rm);
        if (k > -1) { arr.splice(k, 1); }
      });
      v.composedRule.Group = arr;
    }
  
    const addChildRule = (kind: RuleDisplayKind) => {
      setStates(v => {
        if (pickerRules.has(kind)) { return v; }
        v.added.stubs.push({ 
          Kind: kind, 
          Group: groupRules.has(kind) ? [] : undefined,
          ...{n: v.added.n},
        })
        v.added.rules.push(null as any)
        v.added.n += 1
        // recompose(v);
        return {...v}
      })
    }
  
    const removeChildRule = (r: RuleDisplayTree) => {
      setStates(v => {
        if (v.added.stubs.indexOf(r) > -1) {
          const i = v.added.stubs.indexOf(r)
          v.added.stubs.splice(i, 1)
          v.added.rules.splice(i, 1)
        } else if (v.removedRules.indexOf(r) > -1) {
          return v
        } else {
          v.removedRules.push(r)
        }
        recompose(v);
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v) ? rule : v.composedRule))
        return {...v}
      })
    }
    
    const restoreChildRule = (r: RuleDisplayTree) => {
      setStates(v => {
        const i = v.removedRules.indexOf(r)
        if (i < 0) { return v }
        v.removedRules.splice(i, 1)
        recompose(v);
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v) ? rule : v.composedRule))
        return {...v}
      })
    }
    
    const updateChildRule = (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => {
      setStates(v => {
        let i = v.prevGroup.indexOf(prevRule)
        if (i > -1) {
          if (prevRule === updRule) {
            delete v.updatedRules[i];
          } else {
            v.updatedRules[i] = updRule
          }
        } else {
          i = v.added.stubs.indexOf(prevRule)
          if (i > -1) {
            v.added.rules[i] = updRule
          }
        }
        if (i < 0) { return v }
        recompose(v);
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v) ? rule : v.composedRule))
        // return {...v}
        return v
      })
    }
    const valueEqualsPrev = (v: typeof states) => {
      return v.updatedRules.every(x => !x) && v.removedRules.every(x => !x) && v.added.rules.every(x => !x)
    }
    return { addChildRule, removeChildRule, restoreChildRule, updateChildRule }
  }, [setStates, propagateUp, rule])
  return { states, ...modifiers, nextLevel: level + 1, color: colors[level % colors.length] }
}

const colors: string[] = [
  '#42a5f5',
  '#ba68c8',
  '#ff9800',
  '#4caf50',
]

interface NestedGroupContextPros {
  level: number;
  propagateUp: (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => void;
}

const NestedGroupContext = createContext<NestedGroupContextPros>({ propagateUp: () => {}, level: 0 } as any)


const OperatorRule: FC<{rule:RuleDisplayTree}> = ({rule}) => {
  const { 
    setLeft, setRight, setOpKind, 
    states: { multipleValues, composedRule, isValid, errMsg }, 
    leftRef,
    rightRef,
    operatorRef
  } = useOperatorRuleController(rule)
  const { states:{validationTouched} } = useContext(RuleComposeContext)


  return (
    <Stack className="operator-placement" direction="row" alignItems="center" flex="1 1 auto" >
      <Box sx={{ mr: -1 }}>
        {composedRule.Left && <PickValueRule ref={leftRef} rule={composedRule.Left} onPickerChange={setLeft} />}
      </Box>
      <Box sx={{ 
        minWidth: 120,
        '& input': { color: 'info.main' },
        '& fieldset.MuiOutlinedInput-notchedOutline': { display: 'none' }
      }}>
        <Autocomplete disableClearable={true} forcePopupIcon={false} PopperComponent={MyPopper}
          options={opOptions}
          getOptionLabel={s => s.text}
          renderInput={(params) =>
            <TextField {...params} inputRef={operatorRef} />  
          }
          value={opOptions.find(x => x.key === composedRule.Kind) ?? opOptions[0]}
          onChange={(e, value, reason) => {
            if (reason === 'selectOption') {
              if (value) {
                setOpKind(value.key as any)
              }
            }
          }}
        />
      </Box>
      <Box sx={{ minWidth: 200, flex: '1 1 auto', mr: 2 }}>
        {composedRule.Right && (
          multipleValues
          ? <PickConstantsRule ref={rightRef} rule={composedRule.Right!} onPickerChange={setRight} isValid={!validationTouched || isValid} />
          : <PickConstantRule  ref={rightRef} rule={composedRule.Right!} onPickerChange={setRight} isValid={!validationTouched || isValid} />
        )}
        {errMsg &&
          <FormHelperText error>{errMsg}</FormHelperText>}
      </Box>
    </Stack>
  )
}

const opOptions: SimpleOption2[] = [
  { key: "Equals", text: "Equals" },
  { key: "Contains", text: "Contains" },
  { key: "Matches", text: "Matches" },
  { key: "EqualsAny", text: "Equals Any" },
  { key: "MatchesAny", text: "Matches Any" },
]

interface OperatorRuleContextProps {
  setLeft: (r: RuleDisplayTree) => void;
  setRight: (r: RuleDisplayTree) => void;
  setOpKind: (k: RuleDisplayKind) => void;
  states: {
    preRule: RuleDisplayTree;
    multipleValues: boolean;
    composedRule: RuleDisplayTree;
    isValid: boolean;
    errMsg: string | undefined;
  };
  leftRef: MutableRefObject<HTMLInputElement|undefined>;
  rightRef: MutableRefObject<HTMLInputElement|undefined>;
  operatorRef: MutableRefObject<HTMLInputElement|undefined>;
}


const useOperatorRuleController = (rule:RuleDisplayTree): OperatorRuleContextProps => {
  const [states, setStates] = useState({
    preRule: rule,
    multipleValues: rule.Kind === 'EqualsAny' || rule.Kind === 'MatchesAny',
    composedRule: { // this field's obj ref should remain stable
      Kind: rule.Kind, 
      Left: rule.Left ?? { Kind: 'PickValue', Value: 'FileName' }, 
      Right: rule.Right ?? { Kind: 'PickConstant', Value: '' },
    } as RuleDisplayTree, 
    isValid: true,
    errMsg: undefined as string|undefined,
  })
  const { propagateUp } = useContext(NestedGroupContext)
  const leftRef = useRef<HTMLInputElement>()
  const rightRef = useRef<HTMLInputElement>()
  const operatorRef = useRef<HTMLInputElement>()

  const modifiers = useMemo(() => {
    const valueEqualsPrev = (v: typeof states) => {
      if (v.preRule.Kind !== v.composedRule.Kind) { return false }
      const leftEqual = (v.preRule.Left === v.composedRule.Left ||
        (v.preRule.Left?.Kind === v.composedRule.Left?.Kind
          && v.preRule.Left?.Value === v.composedRule.Left?.Value
        )
      )
      if (!leftEqual) { return false }
      if (v.preRule.Right === v.composedRule.Right) { return true }
      if (v.preRule.Right?.Kind === v.composedRule.Right?.Kind) {
        if (v.preRule.Kind === 'EqualsAny' || v.preRule.Kind === 'MatchesAny') {
          const s0 = new Set(v.preRule.Right?.Values ?? [])
          const s1 = new Set(v.composedRule.Right?.Values ?? [])
          const l1 = s1.size
          for (let x of s0) { s1.add(x) }
          return s0.size === s1.size && l1 === s1.size
        }
        return v.preRule.Right?.Value === v.composedRule.Right?.Value
      }
      return false
    }
    const setLeft = (r:RuleDisplayTree) => {
      setStates(v => {
        if (v.composedRule.Left === r) { return v }
        v.composedRule.Left = r
        const errMsg = validatorSync(v.composedRule)
        v.errMsg = errMsg
        v.isValid = !errMsg
        setTimeout(() => 
          propagateUp(v.preRule, 
            valueEqualsPrev(v) ? v.preRule : v.composedRule))
        return {...v}
      })
      operatorRef.current?.focus()
    }
    const setRight = (r:RuleDisplayTree) => {
      setStates(v => {
        if (v.composedRule.Right === r) { return v }
        v.composedRule.Right = r
        const errMsg = validatorSync(v.composedRule)
        v.errMsg = errMsg
        v.isValid = !errMsg
        setTimeout(() => 
          propagateUp(v.preRule, 
            valueEqualsPrev(v) ? v.preRule : v.composedRule))
        return {...v}
      })
    }
    const setOpKind = (k: RuleDisplayKind) => {
      if (!operatorRules.has(k)) return
      setStates(v => {
        if (v.composedRule.Kind === k) { return v }
        v.composedRule.Kind = k
        
        if (k === 'EqualsAny' || k === 'MatchesAny') {
          if (!v.multipleValues) {
            v.multipleValues = true
            v.composedRule.Right = { Kind: 'PickConstants', Values: [] }
          }
        } else {
          if (v.multipleValues) {
            v.multipleValues = false
            v.composedRule.Right = { Kind: 'PickConstant', Value: '' }
          }
        }
        const errMsg = validatorSync(v.composedRule)
        v.errMsg = errMsg
        v.isValid = !errMsg
        setTimeout(() => 
          propagateUp(v.preRule, 
            valueEqualsPrev(v) ? v.preRule : v.composedRule))
        return {...v}
      })
      setTimeout(() => rightRef.current?.focus(), 50)
    }
    const validatorSync = (x: RuleDisplayTree):string|undefined => {
      let valid = Boolean(
        x.Left?.Value && 
        x.Right && (
            x.Right.Kind === 'PickConstant'  ? x.Right.Value
          : x.Right.Kind === 'PickConstants' ? (x.Right.Values && x.Right.Values.length > 0)
          : false
        ))
      if (!valid) { return 'Rule definition incomplete' }
      if (x.Kind === 'Matches') {
        try {
          new RegExp(x.Right!.Value as any)
        } catch {
          return 'Regular Expression value is incorrect'
        }
      } else if (x.Kind === 'MatchesAny') {
        for (const n in x.Right!.Values!) {
          try {
            new RegExp(x.Right!.Values[n]! as any)
          } catch {
            return `Regular Expression value at position: ${n} is incorrect`
          }
        }
      }
    }
    const validator = () => {
      return new Promise<boolean>(resolve => {
        let isValid = true
        setStates(v => {
          let shouldReload = false
          const errMsg = validatorSync(v.composedRule)
          if (v.errMsg !== errMsg) {
            v.errMsg = errMsg
            shouldReload = true
          }
          v.isValid = !errMsg
          isValid = v.isValid
          return shouldReload ? {...v} : v
        })
        resolve(isValid)
      })
    }
    return { setLeft, setRight, setOpKind, validator }
  }, [setStates, propagateUp])

  const { registerValidator, unregisterValidator } = useContext(RuleComposeContext)
  useEffect(() => {
    const n = registerValidator(modifiers.validator)
    return () => {
      unregisterValidator(n)
    }
  }, [registerValidator, unregisterValidator, modifiers.validator])

  return { states, ...modifiers, leftRef, rightRef, operatorRef }
}


interface PickerRuleProps {
  // ref: Ref<any>,
  rule:RuleDisplayTree, 
  onPickerChange:(x:RuleDisplayTree)=>void
}


const PickValueRule = forwardRef<HTMLInputElement|undefined, PickerRuleProps>(({rule, onPickerChange}, ref) => {
  
  return (
    <Box sx={{ 
      minWidth: 120,
      '& input': { color: 'success.main', textAlign: 'right' },
      '& div.MuiInput-root.MuiInput-underline:before': { display: 'none' },
    }}>
      <Autocomplete disableClearable={true} forcePopupIcon={false} PopperComponent={MyPopper}
        options={pvOptions}
        getOptionLabel={s => s.text}
        renderInput={(params) =>
          <TextField {...params} variant="standard" inputRef={ref} sx={{ color: 'success.main' }} />
        }
        value={pvOptions.find(x => x.key === rule.Value) ?? pvOptions[0]}
        onChange={(e, value, reason) => {
          if (reason === 'selectOption') {
            if (value) {
              onPickerChange({...rule, Value: value.key})
            }
          }
        }}
      />
    </Box>
  )
})

const pvOptions: SimpleOption2[] = [
  { key: 'FileName', text: 'File Name' },
  { key: 'FilePath', text: 'File Path' },
  { key: 'FileExtension', text: 'File Extension' },
  { key: 'Computer', text: 'Computer' },
  { key: 'DocumentType', text: 'Document Type' },
  { key: 'InfoTypes', text: 'Info Types' },
  // { key: 'Classification', text: 'Classification' },
]

const MyPopper: FC<any> = (props) => {
  return (<Popper {...props} placement="bottom-start" style={{ width: 130 }} />)
}


const PickConstantRule = forwardRef<HTMLInputElement|undefined, PickerRuleProps&{isValid:boolean}>(
  ({rule, onPickerChange, isValid}, ref) => {

  return (
    <Box sx={{ 
      '& input,textarea': { color: '#ad5324' },
    }}>
      <TextField inputRef={ref} fullWidth multiline error={!isValid}
        value={rule.Value ?? ''}
        onChange={(e) => {
          onPickerChange({...rule, Value: e.target.value})
        }}
      />
    </Box>
  )
})

const PickConstantsRule = forwardRef<HTMLInputElement|undefined, PickerRuleProps&{isValid:boolean}>(
  ({rule, onPickerChange, isValid}, ref) => {

  return (
    <Box sx={{ 
      '& div.MuiOutlinedInput-root > div.MuiChip-root': {
        height: 'auto', minHeight: 32,
        '& > span.MuiChip-label': {
          display: 'block',
          wordBreak: 'break-all',
          whiteSpace: 'break-spaces',
          color: '#ad5324',
        },
      },
    }}>
      <Autocomplete
        multiple={true}
        freeSolo={true}
        options={[]}
        renderInput={(params) => 
          <TextField {...params} inputRef={ref} error={!isValid} />
        } 
        value={(rule.Values ?? []) as string[]}
        onChange={(e, value, reason) => {
          onPickerChange({...rule, Values: value as any})
        }}
      />
    </Box>
  )
})

